import React from "react";
import {
  UserGroupIcon,
  UserIcon,
  InformationCircleIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  StarIcon,
  CalendarIcon,
  ChartPieIcon,
} from "@heroicons/react/24/outline";
import UserCard from "../UserCard";
import Pagination from "../../Pagination";

const UserCardSkeleton = () => (
  <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 animate-pulse">
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 bg-gray-700 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-700 rounded w-3/4" />
        <div className="h-3 bg-gray-700 rounded w-1/2" />
      </div>
    </div>
  </div>
);

const InfoCardSkeleton = () => (
  <div className="bg-gray-800 rounded-xl p-6 animate-pulse space-y-4">
    <div className="h-6 bg-gray-700 rounded w-1/3" />
    <div className="space-y-3">
      <div className="h-4 bg-gray-700 rounded" />
      <div className="h-4 bg-gray-700 rounded w-5/6" />
      <div className="h-4 bg-gray-700 rounded w-4/6" />
    </div>
  </div>
);

const UsersTab = ({
  users,
  selectedUser,
  loading,
  userPage,
  userTotalPages,
  userSearch,
  userAnnouncements,
  userActivitySummary,
  userMonitoringAnalytics,
  onSelectUser,
  onDeselect,
  onPagePrev,
  onPageNext,
  formatDate,
  getStatusBadge,
}) => (
  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
    {/* Users List */}
    <div className="space-y-4 rounded-[10px] p-6 block-bg self-start">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white flex items-center space-x-2">
          <UserGroupIcon className="w-6 h-6" />
          <span>Пайдаланушылар тізімі</span>
        </h3>
        {users.length > 0 && (
          <span className="px-3 py-1 badge-blue text-sm font-medium border">
            <span className="bg-blue-grad bg-clip-text text-transparent">
              {users.length} пайдаланушы
            </span>
          </span>
        )}
      </div>

      {loading && users.length === 0 ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <UserCardSkeleton key={i} />
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="bg-gray-800/50 rounded-xl p-12 text-center border border-gray-700">
          <UserGroupIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-400 mb-2">
            Пайдаланушылар табылмады
          </h4>
          <p className="text-sm text-gray-500">
            {userSearch ? "Іздеу нәтижелері жоқ" : "Пайдаланушылар тізімі бос"}
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
          {users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              isActive={selectedUser?.id === user.id}
              onClick={() => onSelectUser(user.id)}
            />
          ))}
        </div>
      )}

      <Pagination
        page={userPage}
        totalPages={userTotalPages}
        loading={loading}
        onPrev={onPagePrev}
        onNext={onPageNext}
      />
    </div>

    {/* User Details */}
    <div className="space-y-4 block-bg rounded-[10px] p-6 self-start">
      {loading && selectedUser ? (
        <div className="space-y-4">
          <InfoCardSkeleton />
          <InfoCardSkeleton />
          <InfoCardSkeleton />
        </div>
      ) : selectedUser ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white flex items-center space-x-2">
              <InformationCircleIcon className="w-6 h-6" />
              <span>Пайдаланушы мәліметтері</span>
            </h3>
            <button
              onClick={onDeselect}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Basic Info */}
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 space-y-5">
            {/* Avatar + Name + ID */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-700">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-7 h-7 text-white" />
              </div>
              <div className="flex flex-col min-w-0" style={{ textAlign: "left" }}>
                <h4 className="text-xl font-bold text-white leading-tight">
                  {selectedUser.name || "Аты жоқ"}
                </h4>
                <p className="text-sm text-gray-400 mt-0.5">ID: #{selectedUser.id}</p>
              </div>
            </div>

            {/* Fields 2×2 */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div className="flex items-center gap-2 text-gray-300">
                <EnvelopeIcon className="w-6 h-6 text-gray-500 flex-shrink-0" />
                <div style={{ textAlign: "left" }}>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-sm truncate">
                    {selectedUser.email || "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <BuildingStorefrontIcon className="w-6 h-6 text-gray-500 flex-shrink-0" />
                <div style={{ textAlign: "left" }}>
                  <p className="text-xs text-gray-500">Тип</p>
                  <p className="font-medium text-sm capitalize">
                    {selectedUser.user_type || "individual"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <PhoneIcon className="w-6 h-6 text-gray-500 flex-shrink-0" />
                <div style={{ textAlign: "left" }}>
                  <p className="text-xs text-gray-500">Телефон</p>
                  <p className="font-medium text-sm">
                    {selectedUser.phone_number || "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircleIcon
                  className={`w-6 h-6 flex-shrink-0 ${selectedUser.is_active ? "text-green-500" : "text-gray-500"}`}
                />
                <div style={{ textAlign: "left" }}>
                  <p className="text-xs text-gray-500">Статус</p>
                  <p className="font-medium text-sm">
                    {selectedUser.is_active ? "Белсенді" : "Белсенді емес"}
                  </p>
                </div>
              </div>
            </div>

            {/* Balance / Rating */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
              <div className="balance-rating-card rounded-[7px] p-4">
                <div className="flex items-center gap-2 mb-2" style={{ textAlign: "left" }}>
                  <CurrencyDollarIcon className="w-[19px] h-[19px] text-yellow-400 flex-shrink-0" />
                  <p className="text-gray-500" style={{ fontSize: "16px" }}>Баланс</p>
                </div>
                <p className="font-bold text-white leading-none" style={{ fontSize: "30px", textAlign: "center" }}>
                  {selectedUser.balance || 0}{" "}
                  <span style={{ fontSize: "20px", fontWeight: 600 }}>₸</span>
                </p>
              </div>
              <div className="balance-rating-card rounded-[7px] p-4">
                <div className="flex items-center gap-2 mb-2" style={{ textAlign: "left" }}>
                  <StarIcon className="w-[19px] h-[19px] text-yellow-400 flex-shrink-0" />
                  <p className="text-gray-500" style={{ fontSize: "16px" }}>Рейтинг</p>
                </div>
                <p className="font-bold text-white leading-none" style={{ fontSize: "30px", textAlign: "center" }}>
                  {selectedUser.rating || 0}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-700">
              <div className="flex items-center gap-2 text-gray-400">
                <CalendarIcon className="w-5 h-5" />
                <p className="text-sm">
                  <span className="text-gray-500">Құрылған:</span>{" "}
                  {formatDate(selectedUser.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Announcements */}
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
                <BuildingStorefrontIcon className="w-5 h-5" />
                <span>Жарнамалар</span>
              </h4>
              <span className="w-7 h-7 badge-blue border flex items-center justify-center text-sm font-medium flex-shrink-0" style={{ borderRadius: "50%" }}>
                <span className="bg-accent-grad bg-clip-text text-transparent">
                  {userAnnouncements.length}
                </span>
              </span>
            </div>
            {userAnnouncements.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {userAnnouncements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                  >
                    <div className="font-medium text-white mb-2">
                      {announcement.title || "Аты жоқ"}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">
                        <CurrencyDollarIcon className="w-4 h-4 inline mr-1" />
                        {announcement.price} {announcement.currency}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(announcement.status)}`}
                      >
                        {announcement.status || "unknown"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BuildingStorefrontIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Жарнамалар жоқ</p>
              </div>
            )}
          </div>

          {/* Activity Summary */}
          {userActivitySummary && (
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <ChartPieIcon className="w-5 h-5" />
                <span>Белсенділік (соңғы 30 күн)</span>
              </h4>
              {userActivitySummary.action_statistics &&
              Object.keys(userActivitySummary.action_statistics).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(userActivitySummary.action_statistics).map(
                    ([action, count]) => (
                      <div
                        key={action}
                        className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg"
                      >
                        <span className="text-gray-300 capitalize">{action}</span>
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium border border-blue-500/30">
                          {count}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>Белсенділік деректері жоқ</p>
                </div>
              )}
            </div>
          )}

          {/* Monitoring Analytics */}
          {userMonitoringAnalytics && (
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <ChartPieIcon className="w-5 h-5" />
                <span>Мониторинг аналитикасы</span>
              </h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                  <span className="text-gray-300">Барлық оқиғалар</span>
                  <span className="text-2xl font-bold bg-accent-grad bg-clip-text text-transparent">
                    {userMonitoringAnalytics.totalEvents || 0}
                  </span>
                </div>

                {userMonitoringAnalytics.eventsByType &&
                  Object.keys(userMonitoringAnalytics.eventsByType).length >
                    0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-400 mb-3">
                        Оқиғалар түрі бойынша:
                      </p>
                      <div className="space-y-2">
                        {Object.entries(
                          userMonitoringAnalytics.eventsByType,
                        ).map(([type, count]) => (
                          <div
                            key={type}
                            className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg"
                          >
                            <span className="text-gray-300 capitalize">
                              {type}
                            </span>
                            <span className="font-medium text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-700">
                  {userMonitoringAnalytics.firstActivity && (
                    <div className="p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">
                        Алғашқы белсенділік
                      </p>
                      <p className="text-sm font-medium text-gray-300">
                        {formatDate(userMonitoringAnalytics.firstActivity)}
                      </p>
                    </div>
                  )}
                  {userMonitoringAnalytics.lastActivity && (
                    <div className="p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">
                        Соңғы белсенділік
                      </p>
                      <p className="text-sm font-medium text-gray-300">
                        {formatDate(userMonitoringAnalytics.lastActivity)}
                      </p>
                    </div>
                  )}
                </div>

                {userMonitoringAnalytics.topPages?.length > 0 && (
                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm font-medium text-gray-400 mb-3">
                      Ең көп кіру беттері:
                    </p>
                    <div className="space-y-2">
                      {userMonitoringAnalytics.topPages
                        .slice(0, 5)
                        .map((page, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg"
                          >
                            <span className="text-gray-300 truncate">
                              {page.page}
                            </span>
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-medium border border-purple-500/30">
                              {page.count}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center py-16 px-6">
          <div className="text-center">
            <UserIcon className="w-20 h-20 text-gray-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-400 mb-2">
              Пайдаланушы таңдалмады
            </h4>
            <p className="text-sm text-gray-500">
              Мәліметтерді көру үшін сол жақтағы тізімнен пайдаланушыны таңдаңыз
            </p>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default UsersTab;
